import { type ReactElement } from 'react'

import { useTranslate } from '@orus.eu/pharaoh'
import { LoadingRegion } from '@orus.eu/pharaoh-next/components/ui/loading-region'
import { ProductCard } from '@orus.eu/pharaoh-next/components/ui/product-card'
import { Section } from '@orus.eu/pharaoh-next/components/ui/section'
import { Skeleton } from '@orus.eu/pharaoh-next/components/ui/skeleton'
import { View } from '@orus.eu/pharaoh-next/components/ui/view'

import { DownloadListSkeleton } from './components/download-list/download-list-skeleton.tsx'

/**
 * Pending state for `ContractDetailsPage`. Reuses the same `View` / `Section` / `ProductCard`
 * charpente as the loaded page — a bare header, then the establishment section carrying the product
 * card, and the three unconditional titled sections. The crossfade therefore shifts for a customer who
 * also has fiscal attestations, which the skeleton cannot know about: better than reserving height for a
 * section that usually is not there.
 */
export function ContractDetailsPageSkeleton(): ReactElement {
  const translate = useTranslate()

  return (
    <LoadingRegion label={translate('loading_contract')}>
      <View.Root>
        <View.Header>
          <div className="flex items-center gap-40">
            <Skeleton variant="text" width={240} height={36} />
            <Skeleton variant="block" width={120} height={28} radius={9999} />
          </div>
        </View.Header>

        <View.Body>
          <Section.Root>
            <Section.Header>
              <Skeleton variant="text" width={180} height={24} />
              <Section.Subtitle.Skeleton width={280} />
              <Section.Actions>
                <Skeleton variant="button" width={110} height={32} className="max-md:hidden" />
                <Skeleton variant="button" width={32} height={32} className="md:hidden" />
              </Section.Actions>
            </Section.Header>
            <Section.Body>
              <ProductCardSkeleton />
              <ProductCardSkeleton />
            </Section.Body>
          </Section.Root>

          <SectionSkeleton titleWidth={140}>
            <DownloadListSkeleton />
          </SectionSkeleton>

          <SectionSkeleton titleWidth={130}>
            <DownloadListSkeleton />
          </SectionSkeleton>

          <SectionSkeleton titleWidth={110}>
            <Skeleton variant="text" />
            <Skeleton variant="text" />
            <Skeleton variant="text" width="60%" />
          </SectionSkeleton>
        </View.Body>
      </View.Root>
    </LoadingRegion>
  )
}

function ProductCardSkeleton(): ReactElement {
  return (
    <ProductCard.Root>
      <ProductCard.Header>
        <Skeleton variant="block" width={48} height={48} radius={9999} />
        <Skeleton variant="text" width={200} height={24} className="flex-1" />
        <Skeleton variant="block" width={96} height={28} radius={9999} />
      </ProductCard.Header>
      <ProductCard.Content>
        <ProductCard.Body>
          {[0, 1, 2].map((row) => (
            <ProductCard.Row key={row}>
              <ProductCard.RowLabel>
                <Skeleton variant="text" width={160} height={20} />
              </ProductCard.RowLabel>
              <ProductCard.RowValue>
                <Skeleton variant="text" width={64} height={20} />
              </ProductCard.RowValue>
            </ProductCard.Row>
          ))}
        </ProductCard.Body>
        <ProductCard.Actions>
          <Skeleton variant="button" width={150} />
        </ProductCard.Actions>
      </ProductCard.Content>
    </ProductCard.Root>
  )
}

function SectionSkeleton({
  titleWidth,
  children,
}: {
  titleWidth: number
  children: ReactElement | ReactElement[]
}): ReactElement {
  return (
    <Section.Root>
      <Section.Header>
        <Skeleton variant="text" width={titleWidth} height={24} />
      </Section.Header>
      <Section.Body>{children}</Section.Body>
    </Section.Root>
  )
}
